import { useEffect, useState } from "react";
import { getInlineLoader } from "../Helpers";
import { MdEmail, MdLocationOn, MdPhone } from "react-icons/md";
import { IoLogoWhatsapp } from "react-icons/io5";
import banner1 from "../assets/banner1.jpg"; // ✅ Import image at the top

export default function ContactUsContent() {
  const [ready, setReady] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState({
    type: "",
    text: "",
  });

  useEffect(() => {
    setReady(true);
  }, []);

  const highlights = [
    {
      icon: <MdLocationOn size={20} />,
      title: "Based in Arusha",
      text: "Local Tanzanian team ready to craft your perfect adventure.",
      action: () => window.open("https://maps.google.com/?q=Arusha,+Tanzania", "_blank"),
    },
    {
      icon: <MdEmail size={20} />,
      title: "Email Support",
      text: "Share your ideas—receive a personalized reply within 24 hours.",
      action: () => window.open("mailto:info@kwetuafricaadventures.com", "_self"),
    },
    {
      icon: <IoLogoWhatsapp size={20} />,
      title: "WhatsApp Instant",
      text: "Chat live for quick questions, photos, or itinerary tweaks.",
      action: () => window.open("https://wa.me/255676808073", "_blank"),
    },
  ];

  const phoneContacts = [
    {
      label: "+255 676 808 073",
      tel: "tel:+255676808073",
      whatsapp: "https://wa.me/255676808073",
    },
    {
      label: "+255 673 306 622",
      tel: "tel:+255673306622",
      whatsapp: "https://wa.me/255673306622",
    },
  ];

  function updateField(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formStatus.text) {
      setFormStatus({ type: "", text: "" });
    }
  }
async function submitQuickMessage(event) {
  event.preventDefault();

  const name = formData.name.trim();
  const email = formData.email.trim();
  const message = formData.message.trim();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  if (!name || !email || !message) {
    setFormStatus({ type: "error", text: "Please complete every field." });
    return;
  }

  if (!validEmail) {
    setFormStatus({ type: "error", text: "Please supply a valid email address." });
    return;
  }

  try {
    const res = await fetch("/api/send-plan.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        formType: "quick" // ✅ critical
      }),
    });

    const data = await res.json();
    console.log(data); // keep this for debugging

    if (data.status === "success") {
      setFormStatus({ type: "success", text: "Message sent. Expect a reply shortly." });
      setFormData({ name: "", email: "", message: "" });
    } else {
      setFormStatus({ type: "error", text: data.message || "Unable to submit right now." });
    }
  } catch (error) {
    setFormStatus({ type: "error", text: "An unexpected error occurred." });
  }
}

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-emerald-900">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      <section className="contact-page-wrapper">
        {/* Hero Background with imported image */}
        <div 
          className="page-hero"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.7), rgba(22, 78, 99, 0.5)), url(${banner1})`,
          }}
        >
          <div className="hero-content fade-in-up">
            <span className="hero-badge">🌍 Local Experts</span>
            <h1 className="hero-title">Contact Kwetu Africa Adventures</h1>
            <p className="hero-subtitle">
              Your dream safari starts with a conversation. Let's craft something unforgettable together.
            </p>
          </div>
        </div>

        <div className="container content-container">
          {/* Quick Contact Cards */}
          <div className="highlights-grid">
            {highlights.map((item, index) => (
              <button
                key={item.title}
                className="highlight-card slide-in"
                style={{ animationDelay: `${index * 100}ms` }}
                onClick={item.action}
                type="button"
                aria-label={item.title}
              >
                <div className="card-icon">{item.icon}</div>
                <h3 className="card-title">{item.title}</h3>
                <p className="card-text">{item.text}</p>
                <span className="card-arrow">→</span>
              </button>
            ))}
          </div>

          {/* Phone Contacts */}
          <div className="phone-section fade-in" style={{ animationDelay: '300ms' }}>
            <div className="phone-card">
              <div className="phone-header">
                <MdPhone size={18} />
                <span>Call or WhatsApp</span>
              </div>
              <div className="phone-list">
                {phoneContacts.map((phone) => (
                  <div key={phone.label} className="phone-item">
                    <span className="phone-number">{phone.label}</span>
                    <div className="phone-actions">
                      <a href={phone.tel} className="btn-phone" aria-label={`Call ${phone.label}`}>
                        Call
                      </a>
                      <a 
                        href={phone.whatsapp} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="btn-whatsapp"
                        aria-label={`WhatsApp ${phone.label}`}
                      >
                        <IoLogoWhatsapp size={16} />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form Section with imported image + clean overlay */}
          <div 
            className="form-section fade-in"
            style={{ 
              animationDelay: '400ms',
              backgroundImage: `url(${banner1})`,
            }}
          >
            <div className="form-overlay">
              <div className="form-layout">
                {/* Form Info */}
                <div className="form-info">
                  <h2 className="form-title">Let's Plan Your Adventure</h2>
                  <p className="form-description">
                    Tell us your travel dates, group size, and dream experiences. 
                    We'll respond within 24 hours with a custom itinerary.
                  </p>
                  <ul className="form-benefits">
                    <li>✓ Personalized safari & trekking routes</li>
                    <li>✓ Local guides with deep expertise</li>
                    <li>✓ Transparent, all-inclusive pricing</li>
                    <li>✓ Flexible changes until you're happy</li>
                  </ul>
                </div>

                {/* Form */}
                <form onSubmit={submitQuickMessage} className="contact-form">
                  <div className="form-group">
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder="Your Name"
                      className="form-input"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      placeholder="Email Address"
                      className="form-input"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div className="form-group">
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => updateField("message", e.target.value)}
                      placeholder="Share your dream itinerary..."
                      className="form-input form-textarea"
                      required
                      aria-required="true"
                    />
                  </div>
                  <button type="submit" className="form-submit">
                    Send Message
                    <span className="submit-arrow">→</span>
                  </button>
                  
                  {/* Status Messages */}
                  {formStatus.text && (
                    <div className={`form-status ${formStatus.type === 'success' ? 'status-success' : 'status-error'}`}>
                      {formStatus.text}
                      <button 
                        type="button" 
                        className="status-close" 
                        onClick={() => setFormStatus({ type: "", text: "" })}
                        aria-label="Dismiss"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="page-footer">
            <p>
              <MdLocationOn size={14} className="footer-icon" /> 
              Arusha, Tanzania • 
              <a href="mailto:info@kwetuafricaadventures.com" className="footer-link">
                info@kwetuafricaadventures.com
              </a>
            </p>
          </footer>
        </div>
      </section>

      {/* CSS Styles with Smooth Transitions */}
      <style>{`
        /* ===== Base Variables ===== */
        :root {
          --color-primary: #1e6f5c;
          --color-primary-dark: #155a4a;
          --color-secondary: #c9a961;
          --color-bg-light: #ffffff;
          --color-text: #1e293b;
          --color-text-muted: #17182e;
          --color-white: #ffffff;
          --shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
          --shadow-md: 0 4px 12px rgba(0,0,0,0.08);
          --shadow-lg: 0 12px 32px rgba(0,0,0,0.12);
          --radius-sm: 8px;
          --radius-md: 16px;
          --radius-lg: 24px;
          --transition-fast: 150ms ease;
          --transition-normal: 300ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* ===== Page Layout ===== */
        .contact-page-wrapper {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: var(--color-text);
          background: var(--color-bg-light);
          line-height: 1.6;
        }

        .page-hero {
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
          padding: 6rem 2rem 4rem;
          text-align: center;
          color: var(--color-white);
          position: relative;
          overflow: hidden;
        }

        .hero-content {
          max-width: 700px;
          margin: 0 auto;
          position: relative;
        }

        .hero-badge {
          display: inline-block;
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(8px);
          padding: 0.4rem 1rem;
          border-radius: 50rem;
          font-size: 0.85rem;
          font-weight: 500;
          margin-bottom: 1.25rem;
          border: 1px solid rgba(255,255,255,0.2);
        }

        .hero-title {
          font-size: clamp(2rem, 5vw, 3rem);
          font-weight: 700;
          margin: 0 0 1rem;
          line-height: 1.1;
        }

        .hero-subtitle {
          font-size: 1.125rem;
          opacity: 0.95;
          margin: 0;
          max-width: 550px;
          margin-left: auto;
          margin-right: auto;
          text-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        .content-container {
          padding: 3rem 0 4rem;
        }

        /* ===== Animations ===== */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(24px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-16px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .fade-in-up {
          animation: fadeInUp 0.6s var(--transition-normal) forwards;
        }

        .fade-in {
          animation: fadeInUp 0.5s var(--transition-normal) forwards;
          opacity: 0;
        }

        .slide-in {
          animation: slideIn 0.4s var(--transition-normal) forwards;
          opacity: 0;
        }

        /* ===== Highlight Cards ===== */
        .highlights-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 3rem;
        }

        .highlight-card {
          background: var(--color-white);
          border: 1px solid rgba(0,0,0,0.06);
          border-radius: var(--radius-md);
          padding: 1.5rem;
          text-align: left;
          cursor: pointer;
          transition: all var(--transition-normal);
          position: relative;
          overflow: hidden;
        }

        .highlight-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          transform: scaleX(0);
          transform-origin: left;
          transition: transform var(--transition-normal);
        }

        .highlight-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-lg);
          border-color: rgba(30, 111, 92, 0.2);
        }

        .highlight-card:hover::before {
          transform: scaleX(1);
        }

        .card-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--color-primary), var(--color-primary-dark));
          color: white;
          border-radius: var(--radius-sm);
          margin-bottom: 1rem;
        }

        .card-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin: 0 0 0.5rem;
          color: var(--color-text);
        }

        .card-text {
          font-size: 0.95rem;
          color: var(--color-text-muted);
          margin: 0 0 1rem;
          line-height: 1.5;
        }

        .card-arrow {
          position: absolute;
          bottom: 1rem;
          right: 1.5rem;
          color: var(--color-primary);
          font-weight: 600;
          opacity: 0;
          transform: translateX(-8px);
          transition: all var(--transition-fast);
        }

        .highlight-card:hover .card-arrow {
          opacity: 1;
          transform: translateX(0);
        }

        /* ===== Phone Section ===== */
        .phone-section {
          margin-bottom: 3rem;
        }

        .phone-card {
          background: var(--color-white);
          border-radius: var(--radius-md);
          padding: 1.25rem 1.5rem;
          border: 1px solid rgba(0,0,0,0.06);
          box-shadow: var(--shadow-md);
        }

        .phone-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: var(--color-text);
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }

        .phone-list {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: center;
        }

        .phone-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.5rem 0;
        }

        .phone-number {
          font-weight: 500;
          color: var(--color-text);
          font-size: 0.95rem;
        }

        .phone-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-phone,
        .btn-whatsapp {
          padding: 0.4rem 0.9rem;
          border-radius: 50rem;
          font-size: 0.85rem;
          font-weight: 500;
          text-decoration: none;
          transition: all var(--transition-fast);
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }

        .btn-phone {
          background: var(--color-white);
          color: var(--color-primary);
          border: 1px solid var(--color-primary);
        }

        .btn-phone:hover {
          background: var(--color-primary);
          color: white;
        }

        .btn-whatsapp {
          background: #25D366;
          color: white;
          border: 1px solid #25D366;
        }

        .btn-whatsapp:hover {
          background: #128C7E;
          border-color: #128C7E;
          transform: translateY(-1px);
        }

        /* ===== Form Section - Clean Modern Design ===== */
        .form-section {
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
          border-radius: var(--radius-lg);
          overflow: hidden;
          margin: 2rem 0;
          position: relative;
        }

        .form-overlay {
          /* Dark overlay as requested */
          background: rgba(0,0,0,.5);
          padding: 2.5rem;
          position: relative;
        }

        .form-overlay::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
        }

        .form-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          max-width: 900px;
          margin: 0 auto;
        }

        @media (min-width: 900px) {
          .form-layout {
            grid-template-columns: 1fr 1.2fr;
            align-items: start;
          }
        }

        .form-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 0.75rem;
          color: white;
        }

        .form-description {
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 1.5rem;
          font-size: 1rem;
        }

        .form-benefits {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .form-benefits li {
          padding: 0.35rem 0;
          color: rgba(255, 255, 255, 0.85);
          font-size: 0.95rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          background:rgba(255,255,255,.15);
          padding:10px;
          border-radius:10px;
          backdrop-filter:blur;
        }

        .form-group {
          position: relative;
        }

.form-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 2px solid #ffffff;
          border-radius: var(--radius-sm);
          font-size: 1rem;
          transition: all var(--transition-fast);
          background: rgba(255,255,255,.15) !important;
          color: white;
        }

        .form-input:focus {
          border-color: #9ca3af !important;
          box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.3) !important;
        }

        .form-input::placeholder {
          color: rgb(20, 24, 20) !important;
          opacity: 1;
          font-weight:bold;
        }

.form-textarea {
          min-height: 120px;
          resize: vertical;
          color: white;
        }

        .form-submit {
          background: linear-gradient(135deg, var(--color-primary), var(--color-primary-dark));
          color: white;
          border: none;
          padding: 1rem 1.5rem;
          border-radius: var(--radius-sm);
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all var(--transition-normal);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }

        .form-submit:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(30, 111, 92, 0.3);
        }

        .form-submit:active {
          transform: translateY(0);
        }

        .submit-arrow {
          transition: transform var(--transition-fast);
        }

        .form-submit:hover .submit-arrow {
          transform: translateX(4px);
        }

        /* ===== Form Status ===== */
        .form-status {
          padding: 0.875rem 1rem;
          border-radius: var(--radius-sm);
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          animation: fadeInUp 0.3s ease;
        }

        .status-success {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .status-error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .status-close {
          background: none;
          border: none;
          font-size: 1.25rem;
          cursor: pointer;
          color: inherit;
          opacity: 0.7;
          transition: opacity var(--transition-fast);
          padding: 0;
          line-height: 1;
        }

        .status-close:hover {
          opacity: 1;
        }

        /* ===== Footer ===== */
        .page-footer {
          text-align: center;
          padding: 2rem 0;
          color: var(--color-text-muted);
          font-size: 0.9rem;
        }

        .footer-icon {
          display: inline-flex;
          align-items: center;
          margin-right: 0.25rem;
          vertical-align: middle;
        }

        .footer-link {
          color: var(--color-primary);
          text-decoration: none;
          margin-left: 0.25rem;
          transition: color var(--transition-fast);
        }

        .footer-link:hover {
          color: var(--color-primary-dark);
          text-decoration: underline;
        }

        /* ===== Responsive ===== */
        @media (max-width: 768px) {
          .page-hero {
            padding: 4rem 1.5rem 3rem;
          }
          
          .hero-title {
            font-size: 2.25rem;
          }
          
          .content-container {
            padding: 2rem 0 3rem;
          }
          
          .form-overlay {
            padding: 1.75rem;
          }
          
          .form-title {
            font-size: 1.35rem;
          }
        }

        /* ===== Utility ===== */
        * {
          box-sizing: border-box;
        }

        button {
          font: inherit;
        }

        a {
          color: inherit;
        }

        img {
          max-width: 100%;
          height: auto;
        }
      `}</style>
    </>
  );
}